import type {GraphAtom} from "./GraphAtom";

/** Halogens that get the "H first" exception when paired 1:1 with H (HCl, HBr, ...). */
const HYDROGEN_HALIDE_HALOGENS = new Set(["F", "Cl", "Br", "I"]);

/**
 * Hill-system molecular formula from atom symbols + implicit H totals.
 *
 * Conventional-ordering overrides (PIPH-319):
 * - Hydrogen halides (1 H + 1 F/Cl/Br/I) write H first: "HBr", not "BrH".
 * - Ammonia (1 N + 3 H, no C) writes N first: "NH3", not "H3N".
 */
export const getMolecularFormula = (atoms: GraphAtom[], includeH: boolean): string => {
    const counts: Record<string, number> = {};
    for (const a of atoms) {
        counts[a.symbol] = (counts[a.symbol] || 0) + 1;
        if (includeH && a.implicitH > 0) {
            counts["H"] = (counts["H"] || 0) + a.implicitH;
        }
    }

    const symbols = Object.keys(counts);

    // PIPH-319: Hydrogen halide override (HF, HCl, HBr, HI). Exactly two
    // elements, 1 H and 1 halogen.
    if (
        symbols.length === 2 &&
        counts["H"] === 1 &&
        symbols.some(s => HYDROGEN_HALIDE_HALOGENS.has(s) && counts[s] === 1)
    ) {
        const halogen = symbols.find(s => HYDROGEN_HALIDE_HALOGENS.has(s))!;
        return `H${halogen}`;
    }

    // PIPH-319: Ammonia override (NH3). N + multiple H, no other elements.
    if (symbols.length === 2 && counts["N"] === 1 && counts["H"] && !counts["C"]) {
        return "N" + (counts["H"] > 1 ? `H${counts["H"]}` : "H");
    }

    // Hill system: C first, then H, then alphabetical
    const parts: string[] = [];
    if (counts["C"]) {
        parts.push("C" + (counts["C"] > 1 ? counts["C"] : ""));
        delete counts["C"];
        if (counts["H"]) {
            parts.push("H" + (counts["H"] > 1 ? counts["H"] : ""));
            delete counts["H"];
        }
    }

    const remaining = Object.keys(counts).sort();
    for (const sym of remaining) {
        parts.push(sym + (counts[sym] > 1 ? counts[sym] : ""));
    }

    return parts.join("");
};
